import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import { AppTextService } from '@fi-shared/app-text';
import { DocumentService } from '@fi-app/document/document.service';
import { FiSnackBarService } from '@fi-shared/fi-shared-services';
import { DocumentModel } from '@fi-app/document/document.model';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { ILightWeightTemplate } from '@fi-app/document-template/document-template';
import { IDocumentDefaultTemplateSettings } from '@fi-app/document/document';
import { TemplateLayoutModel } from '@fi-app/template/template-layout.model';

@Component({
    selector: 'fi-document-edit-template',
    templateUrl: './template.component.html',
    styleUrls: ['./template.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-document-edit-template' },
    standalone: false,
})
export class DocumentEditTemplateComponent implements OnInit, OnChanges {
    @Input() document: DocumentModel;
    @Input() defaultTemplateSettings: IDocumentDefaultTemplateSettings;
    @Input() documentTemplateLayout: TemplateLayoutModel;
    @Input() documentTemplates: ILightWeightTemplate[];

    selectedTemplate: ILightWeightTemplate;
    selectedTemplateId: number;
    initialDocumentTemplateLayout: string;
    templates: ILightWeightTemplate[] = [];
    templatesFiltered: any[] = [];

    isOptionDisabledFn = (item: ILightWeightTemplate) => !item.HasViewAccess;

    constructor(
        private appTextService: AppTextService,
        private documentService: DocumentService,
        private snackBar: FiSnackBarService,
        private fiDialogsService: FiDialogsService,
    ) {}

    ngOnInit() {
        this.templates = this.documentTemplates.filter(
            (template: ILightWeightTemplate) => template.HasViewAccess || this.document.TemplateId == template.Id,
        );

        this.selectedTemplateId = this.document.TemplateId =
            !this.document.TemplateId && this.templates.length > 0 ? this.templates[0].Id : this.document.TemplateId;

        // todo implement <fi-material-dropdown> - copy of current fi-dropdown using mat-select
        /*          var templatesWithAcccess = response.filter(template => template.HasViewAccess);
                    this.TemplatesDropdownData.AccessRightFilter = templatesWithAcccess.map(template => template.Id);
                    this.templateDropdown.setDataSet(response);
        */
        this.updateSelectedTemplate();
        this.updateFilteredTemplates();
    }

    saveDefaultTemplate($event) {
        $event.preventDefault();
        const data = { TemplateId: this.document.TemplateId },
            dataUndo = this.defaultTemplateSettings ? { TemplateId: this.defaultTemplateSettings.TemplateId } : null;
        this.documentService.SaveTemplateAsDefault(data).then(() => {
            this.defaultTemplateSettings = data;
            this.snackBar
                .showSnackbar({
                    textMessage: this.appTextService.get('document.default_template_updated'),
                    buttonText: this.appTextService.get('common.undo'),
                })
                .onAction()
                .subscribe(() => {
                    this.defaultTemplateSettings = dataUndo;
                    this.documentService.SaveTemplateAsDefault(dataUndo);
                });
        });
    }

    templateOnChange($event: number) {
        this.templateOnChangeConfirm().then((result: boolean) => {
            this.selectedTemplate = this.templates.find(
                (item: ILightWeightTemplate) => item.Id === this.document.TemplateId,
            );
            if (result) {
                this.selectedTemplateId = this.document.TemplateId = $event;
                this.updateSelectedTemplate();
            } else {
                this.selectedTemplateId = this.selectedTemplate.Id;
            }
        });
    }

    updateSelectedTemplate() {
        this.selectedTemplate = this.templates.find(
            (item: ILightWeightTemplate) => item.Id === this.selectedTemplateId,
        );
    }

    private templateOnChangeConfirm(): Promise<boolean> {
        // check before change template. If document content has been changed - user have to confirm change
        if (this.initialDocumentTemplateLayout === JSON.stringify(this.documentTemplateLayout))
            return Promise.resolve(true);

        return new Promise<boolean>((resolve) => {
            const dialog = this.fiDialogsService.confirm({
                messageText: this.appTextService.get('document.template_change_confirm_message'),
                headerText: this.appTextService.get('document.template_change_confirm_header'),
            });
            dialog.onYesButtonClick(() => resolve(true)).onNoButtonClick(() => resolve(false));
        });
    }

    isSaveDefaultDisabled(): boolean {
        return this.defaultTemplateSettings && this.defaultTemplateSettings.TemplateId === this.document.TemplateId;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.hasOwnProperty('documentTemplateLayout'))
            this.initialDocumentTemplateLayout = JSON.stringify(changes.documentTemplateLayout.currentValue);
    }

    private _searchText: string = '';

    get searchText(): string {
        return this._searchText;
    }

    set searchText(value: string) {
        this._searchText = value;
        this.updateFilteredTemplates();
    }

    updateFilteredTemplates() {
        this.templatesFiltered = this.templates.filter(
            (template: ILightWeightTemplate) =>
                !this.searchText || template.Name.toLowerCase().indexOf(this.searchText.toLowerCase()) > -1,
        );
    }
}
